// Aptos mainnet data. Live chain state (validator set, stake, proposal performance,
// epoch) is read from the fullnode REST API on every refresh. Operators, governance
// votes and locations change rarely and are expensive to query, so they come from a
// snapshot built by scripts/update-snapshot.mjs — or live when an API key is set.
//
// Shared by the React app and the Node snapshot script, so no bundler-only imports.

const NODE_API = 'https://api.mainnet.aptoslabs.com/v1';
const INDEXER_API = 'https://api.mainnet.aptoslabs.com/v1/graphql';
const EXPLORER_STATS = 'https://storage.googleapis.com/aptos-mainnet/explorer/validator_stats_v2.json';

// Anonymous browser access is limited to 25k compute units per 5 min per IP and origin.
// An Aptos Build key (https://build.aptoslabs.com) lifts that; restrict it to your
// site's origin, since it ships to the browser.
const API_KEY = process.env.REACT_APP_APTOS_API_KEY || process.env.APTOS_API_KEY;
const SNAPSHOT_URL = `${process.env.PUBLIC_URL || ''}/data/validator-snapshot.json`;

const OCTAS_PER_APT = 1e8;
const MICROS_PER_YEAR = 365 * 24 * 3600 * 1e6;
const LIVE_SLOW_DATA_TTL = 15 * 60_000;
export const GOVERNANCE_WINDOW = 20;

export class RateLimitError extends Error {
    constructor() {
        super('The public Aptos API is rate-limiting this browser. Data will load again within a few minutes.');
    }
}

export async function getJson(url, options = {}) {
    const headers = {...options.headers};
    if (API_KEY && url.startsWith(NODE_API)) headers.authorization = `Bearer ${API_KEY}`;
    const res = await fetch(url, {...options, headers});
    // The limit window is five minutes, so retrying straight away only burns more budget.
    if (res.status === 429) throw new RateLimitError();
    if (!res.ok) throw new Error(`${res.status} ${res.statusText} — ${url}`);
    return res.json();
}

const resource = (type, address = '0x1') => getJson(`${NODE_API}/accounts/${address}/resource/${type}`).then(r => r.data);

async function graphql(query, variables) {
    const body = await getJson(INDEXER_API, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify({query, variables}),
    });
    if (body.errors) throw new Error(body.errors[0].message);
    return body.data;
}

export const fetchValidatorSet = () => resource('0x1::stake::ValidatorSet');

export async function fetchOperators(pools) {
    // The indexer returns at most 100 rows per query.
    const rows = [];
    for (let offset = 0; ; offset += 100) {
        const {current_staking_pool_voter: page} = await graphql(
            `query ($pools: [String!], $offset: Int) {
                current_staking_pool_voter(
                    where: {staking_pool_address: {_in: $pools}}
                    order_by: {staking_pool_address: asc}, offset: $offset, limit: 100
                ) {
                    staking_pool_address operator_address voter_address
                    operator_aptos_name { domain }
                }
            }`,
            {pools, offset},
        );
        rows.push(...page);
        if (page.length < 100) break;
    }
    const operators = Object.fromEntries(rows.map(r => [r.staking_pool_address, {
        operator: r.operator_address,
        voter: r.voter_address,
        name: r.operator_aptos_name?.[0]?.domain ? `${r.operator_aptos_name[0].domain}.apt` : null,
    }]));

    // The indexer misses some pools; read those straight from their on-chain StakePool.
    for (const pool of pools.filter(p => !operators[p])) {
        const pool$ = await resource('0x1::stake::StakePool', pool).catch(() => null);
        if (pool$) operators[pool] = {operator: pool$.operator_address, voter: pool$.delegated_voter, name: null};
    }
    return operators;
}

export async function fetchGovernance(pools) {
    const {latest} = await graphql(`{ latest: proposal_votes(limit: 1, order_by: {proposal_id: desc}) { proposal_id } }`);
    const latestId = latest[0]?.proposal_id ?? 0;
    const firstId = Math.max(0, latestId - GOVERNANCE_WINDOW + 1);
    // One aliased distinct-count aggregate per pool, which sidesteps the 100-row cap.
    const fields = pools.map((pool, i) => `p${i}: proposal_votes_aggregate(
        where: {staking_pool_address: {_eq: "${pool}"}, proposal_id: {_gte: ${firstId}}}
    ) { aggregate { count(columns: proposal_id, distinct: true) } }`);
    const data = await graphql(`{ ${fields.join('\n')} }`);
    const counts = Object.fromEntries(pools.map((pool, i) => [pool, data[`p${i}`].aggregate.count]));
    return {counts, window: latestId - firstId + 1, latestId};
}

const liveCache = {};
function cachedLive(key, load) {
    const hit = liveCache[key];
    if (hit && Date.now() - hit.at < LIVE_SLOW_DATA_TTL) return hit.promise;
    const promise = load();
    liveCache[key] = {at: Date.now(), promise};
    promise.catch(() => delete liveCache[key]);
    return promise;
}

let snapshotPromise;
async function loadSlowData(pools) {
    snapshotPromise ??= Promise.all([
        getJson(SNAPSHOT_URL).catch(() => ({locations: {}, operators: {}, governance: null})),
        getJson(EXPLORER_STATS).catch(() => []),
    ]);
    const [snapshot, explorer] = await snapshotPromise;

    // Fall back to Aptos' explorer location feed for validators missing from the snapshot.
    const locations = {...snapshot.locations};
    explorer.forEach(({location_stats: l}) => {
        if (l?.peer_id && !locations[l.peer_id] && l.latitude != null) {
            locations[l.peer_id] = {
                city: l.city, country: l.country, countryCode: null, continent: null,
                latitude: l.latitude, longitude: l.longitude, provider: null, host: null,
            };
        }
    });

    let {operators, governance} = snapshot;
    let source = {slowData: 'snapshot', generatedAt: snapshot.generatedAt};
    if (API_KEY) {
        const key = [...pools].sort().join();
        const [liveOperators, liveGovernance] = await Promise.all([
            cachedLive(`operators:${key}`, () => fetchOperators(pools)).catch(() => null),
            cachedLive(`governance:${key}`, () => fetchGovernance(pools)).catch(() => null),
        ]);
        if (liveOperators && liveGovernance) source = {slowData: 'live', generatedAt: null};
        operators = liveOperators || operators;
        governance = liveGovernance || governance;
    }
    return {locations, operators: operators || {}, governance, source};
}

// Fewest validators whose combined voting power exceeds 1/3 — enough to halt consensus.
function nakamotoCoefficient(validators) {
    const total = validators.reduce((s, v) => s + v.stake, 0);
    const sorted = [...validators].sort((a, b) => b.stake - a.stake);
    let acc = 0;
    for (let i = 0; i < sorted.length; i++) {
        acc += sorted[i].stake;
        if (acc > total / 3) return i + 1;
    }
    return sorted.length;
}

function tally(validators, key) {
    const counts = {};
    validators.forEach(v => {
        const k = key(v) || 'Unknown';
        counts[k] = (counts[k] || 0) + 1;
    });
    return Object.entries(counts).map(([label, count]) => ({label, count})).sort((a, b) => b.count - a.count);
}

export async function fetchNetwork() {
    const [ledger, set, perf, stakingConfig, reconfig, block] = await Promise.all([
        getJson(NODE_API),
        fetchValidatorSet(),
        resource('0x1::stake::ValidatorPerformance'),
        resource('0x1::staking_config::StakingConfig'),
        resource('0x1::reconfiguration::Configuration'),
        resource('0x1::block::BlockResource'),
    ]);

    const pools = set.active_validators.map(v => v.addr);
    const {locations, operators, governance, source} = await loadSlowData(pools);

    const totalPower = Number(set.total_voting_power);
    const validators = set.active_validators.map(v => {
        const p = perf.validators[Number(v.config.validator_index)] || {};
        const successful = Number(p.successful_proposals || 0);
        const failed = Number(p.failed_proposals || 0);
        const votes = governance?.counts[v.addr];
        return {
            address: v.addr,
            stake: Number(v.voting_power) / OCTAS_PER_APT,
            votingShare: Number(v.voting_power) / totalPower,
            successful,
            failed,
            successRate: successful + failed ? successful / (successful + failed) : null,
            governanceVotes: votes ?? null,
            governanceRate: votes == null ? null : votes / governance.window,
            ...operators[v.addr],
            location: locations[v.addr] || null,
        };
    });

    const epochInterval = Number(block.epoch_interval);
    const totalProposals = validators.reduce((s, v) => s + v.successful + v.failed, 0);
    const totalSuccessful = validators.reduce((s, v) => s + v.successful, 0);
    const located = validators.filter(v => v.location);
    const withGovernance = validators.filter(v => v.governanceRate != null);

    return {
        fetchedAt: Date.now(),
        source,
        chain: {
            epoch: Number(reconfig.epoch),
            blockHeight: Number(ledger.block_height),
            epochEnd: (Number(reconfig.last_reconfiguration_time) + epochInterval) / 1000,
        },
        validators,
        stats: {
            count: validators.length,
            totalStake: totalPower / OCTAS_PER_APT,
            apr: (Number(stakingConfig.rewards_rate) / Number(stakingConfig.rewards_rate_denominator))
                * (MICROS_PER_YEAR / epochInterval),
            proposalSuccess: totalProposals ? totalSuccessful / totalProposals : null,
            totalProposals,
            governanceParticipation: withGovernance.length
                ? withGovernance.reduce((s, v) => s + v.governanceRate, 0) / withGovernance.length
                : null,
            governanceWindow: governance?.window,
            nakamoto: nakamotoCoefficient(validators),
            countries: new Set(located.map(v => v.location.country).filter(Boolean)).size,
            located: located.length,
        },
        breakdowns: {
            continents: tally(validators, v => v.location?.continent),
            countries: tally(validators, v => v.location?.country),
            providers: tally(validators, v => v.location?.provider),
        },
    };
}
